import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { X, Send, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';

/**
 * Reply Panel - Dark/Light Mode Support
 * 
 * Expandable panel for "Reply and wait" action.
 */
const ReplyPanel = ({
  isOpen,
  onClose,
  replyDraft,
  onReplyChange,
  onGenerateReply,
  onSendReply,
  actionInProgress,
  darkMode = false
}) => {
  const { t, i18n } = useTranslation('inbox');
  const isEnglish = i18n.language === 'en' || i18n.language?.startsWith('en');
  
  if (!isOpen) return null;
  
  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      className={`rounded-xl overflow-hidden ${
        darkMode 
          ? 'bg-white/5 border border-white/10' 
          : 'bg-white border border-gray-200'
      }`}
    >
      <div className={`px-4 py-2.5 border-b flex items-center justify-between ${
        darkMode 
          ? 'bg-white/5 border-white/10' 
          : 'bg-gray-50 border-gray-200'
      }`}>
        <span className={`text-sm font-semibold ${
          darkMode ? 'text-white' : 'text-gray-800'
        }`}>
          {isEnglish ? 'Reply to Client' : 'Svar kunde'}
        </span>
        <button 
          type="button" 
          onClick={onClose} 
          className={`${
            darkMode ? 'text-slate-400 hover:text-white' : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="p-4 space-y-3">
        <textarea
          value={replyDraft}
          onChange={(e) => onReplyChange(e.target.value)}
          className={`w-full h-32 p-3 text-sm rounded-lg focus:outline-none focus:ring-2 resize-none ${
            darkMode 
              ? 'bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:ring-violet-500/50' 
              : 'bg-white border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-indigo-500'
          }`}
          placeholder={isEnglish ? "Write reply or click to generate AI draft..." : "Skriv svar eller klikk for AI-generert utkast..."}
        />
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            className={`flex-1 ${
              darkMode 
                ? 'border-white/20 text-slate-300 hover:bg-white/10 hover:text-white bg-transparent' 
                : 'hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-700'
            }`}
            onClick={onGenerateReply}
            disabled={actionInProgress === 'generate-reply'}
          >
            {actionInProgress === 'generate-reply' ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
            ) : (
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            )}
            {isEnglish ? 'Generate Reply' : 'Generer svar'}
          </Button>
          <Button 
            size="sm" 
            className={`flex-1 ${
              darkMode 
                ? 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500' 
                : 'bg-gray-900 hover:bg-gray-800'
            }`}
            onClick={onSendReply}
            disabled={!replyDraft.trim() || actionInProgress === 'send-reply'}
          >
            {actionInProgress === 'send-reply' ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
            ) : (
              <Send className="w-3.5 h-3.5 mr-1.5" />
            )}
            {isEnglish ? 'Send & Wait' : 'Send og vent'}
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default ReplyPanel;
